/**
 * CHiATO Theme — theme.js
 * Vanilla JS, no framework. Module-style IIFE.
 * All components communicate via data attributes and custom events.
 */
(function () {
  'use strict';

  /* ============================================================
     CONSTANTS
     ============================================================ */
  const TOAST_DURATION = 2600;        // ms

  /* ============================================================
     UTILITY HELPERS
     ============================================================ */

  /**
   * Format cents in the shopper's active currency, via layout/theme.liquid's helper.
   */
  function formatMoney(cents) {
    // Delegates to the shared active-market formatter (layout/theme.liquid).
    // The old hardcoded '€' printed a euro sign on GBP amounts for the fixed-GBP
    // UK market. If the global is ever absent the fallback names the ACTIVE currency by its
    // ISO code (Shopify.currency.active) - never a guessed symbol, never the base currency.
    if (window.CHiATO_formatMoney) { return window.CHiATO_formatMoney(cents); }
    return (cents / 100).toFixed(2) + ' ' + ((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '');
  }

  /**
   * Trap focus inside an element (for accessibility in modals/drawers).
   */
  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    }

    container.addEventListener('keydown', handler);
    first.focus();
    return function () { container.removeEventListener('keydown', handler); };
  }

  /**
   * Emit a custom event on an element.
   */
  function emit(el, name, detail) {
    el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }


  /* ============================================================
     HEADER — sticky + mega-menu hover
     ============================================================ */


  /* ============================================================
     MOBILE MENU — full-screen overlay
     ============================================================ */


  /* ============================================================
     SEARCH OVERLAY
     ============================================================ */
  function initSearchOverlay() {
    const openBtns = document.querySelectorAll('[data-search-open]');
    const overlay = document.querySelector('[data-search-overlay]');
    if (!overlay) return;

    const closeBtns = overlay.querySelectorAll('[data-search-close]');
    const input = overlay.querySelector('[data-search-input]');
    let releaseFocus;

    function open() {
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      releaseFocus = trapFocus(overlay);
      if (input) setTimeout(function () { input.focus(); }, 50);
      emit(document, 'chiato:search-open');
    }

    function close() {
      overlay.hidden = true;
      document.body.style.overflow = '';
      if (releaseFocus) releaseFocus();
      emit(document, 'chiato:search-close');
    }

    openBtns.forEach(function (btn) { btn.addEventListener('click', open); });
    closeBtns.forEach(function (btn) { btn.addEventListener('click', close); });

    // Close when clicking the dark area (overlay root or its backdrop child)
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !overlay.hidden) close();
    });
  }


  /* ============================================================
     CART DRAWER — open/close + AJAX cart
     ============================================================ */
  var CartState = {
    items: [],
    item_count: 0,
    total_price: 0,
  };

  function fetchCart() {
    return fetch(window.Shopify.routes.root + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        CartState = cart;
        return cart;
      });
  }

  function addToCart(variantId, quantity, properties) {
    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity, properties: properties }),
    })
      .then(function (r) {
        // 422 (sold out / limit) still returns JSON — reject so callers' .catch fires.
        return r.json().then(function (data) {
          if (!r.ok) { throw new Error((data && data.description) || 'Add to cart failed'); }
          return data;
        });
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        notifyCartUpdated(cart);
        return cart;
      });
  }

  function changeCartItem(key, quantity) {
    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        CartState = cart;
        notifyCartUpdated(cart);
        return cart;
      });
  }

  /* The cart badge is #chiato-cart-badge and belongs to sections/header.liquid, which
     redraws it on the 'cart:updated' event. The old updateCartBadge() here wrote to
     [data-cart-count], an attribute that appears in NO markup anywhere in this theme —
     it survived the dead-code sweep only because the reachability scan saw the selector
     STRING inside other files' inline JS and took that for markup. Notifying instead of
     writing keeps the public API correct without a second badge implementation. */
  /* detail IS the cart object, matching cart-drawer and cart-main. */
  function notifyCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }


  /* ============================================================
     FREE SHIPPING PROGRESS BAR (standalone, e.g. on a cart page)
     ============================================================ */


  /* ============================================================
     TOAST NOTIFICATION
     ============================================================ */
  function showToast(message, icon) {
    const container = document.getElementById('toast-container');
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = [
      icon ? '<span class="toast__icon"><i class="' + icon + '"></i></span>' : '<span class="toast__icon"><i class="fa-solid fa-circle-check"></i></span>',
      '<span>' + escHtml(message) + '</span>',
    ].join('');

    container.appendChild(toast);

    setTimeout(function () {
      toast.style.transition = 'opacity 0.3s ease';
      toast.style.opacity = '0';
      setTimeout(function () { toast.remove(); }, 300);
    }, TOAST_DURATION);
  }

  // Listen for external toast triggers
  document.addEventListener('chiato:toast', function (e) {
    showToast(e.detail.message, e.detail.icon);
  });


  /* ============================================================
     QUANTITY +/- BUTTONS (generic, outside cart drawer)
     ============================================================ */


  /* ============================================================
     RECENTLY VIEWED — localStorage based
     ============================================================ */
  // Entries are { id, handle } records, matching assets/wishlist.js. The id is the
  // identity because product handles are translatable — keyed on the handle, the
  // same product viewed in two languages was two different entries, so it appeared
  // twice in the strip and the "exclude the product you are looking at" filter
  // missed it. The handle rides along because the Section Rendering API that draws
  // the card is addressed by handle; Liquid cannot look a product up by id.
  var RecentlyViewed = {
    _key: 'chiato_recently_viewed',
    _max: 8,

    get: function () {
      try {
        const raw = JSON.parse(localStorage.getItem(this._key)) || [];
        if (!Array.isArray(raw)) return [];
        // Migrate the older bare-handle format in place rather than discarding it.
        return raw.map(function (e) {
          if (e && typeof e === 'object') return { id: e.id == null ? null : Number(e.id), handle: e.handle || null };
          if (typeof e === 'string' && e) return { id: null, handle: e };
          return null;
        }).filter(function (e) { return e && (e.id != null || e.handle); });
      } catch (_) { return []; }
    },

    push: function (id, handle) {
      const list = this.get().filter(function (e) {
        return !((id != null && e.id != null && String(e.id) === String(id)) || (handle && e.handle === handle));
      });
      list.unshift({ id: id == null ? null : Number(id), handle: handle || null });
      if (list.length > this._max) list.length = this._max;
      try { localStorage.setItem(this._key, JSON.stringify(list)); }
      catch (_) {}
    },

    list: function () { return this.get(); },
  };

  function initRecentlyViewed() {
    // Record the current product view. The attribute is data-pdp-handle, which maps
    // to dataset.pdpHandle (not .productHandle — that was always undefined). Present
    // on both PDP templates, so every product is tracked. This is the single tracker;
    // the recently-viewed section only reads and renders.
    // Honour the "Enable recently viewed (localStorage)" setting, surfaced via
    // data-track-recent — turning it off must stop the client-side write.
    const pdpEl = document.querySelector('[data-pdp-handle]');
    const productHandle = pdpEl && pdpEl.dataset.pdpHandle;
    const productId = pdpEl && pdpEl.dataset.pdpId;
    if (productHandle && pdpEl.dataset.trackRecent !== 'false') {
      RecentlyViewed.push(productId, productHandle);
    }

    // Expose for section rendering
    window.CHiATO = window.CHiATO || {};
    window.CHiATO.recentlyViewed = RecentlyViewed.list();
  }


  /* ============================================================
     ACCORDION / COLLAPSIBLE
     ============================================================ */


  /* ============================================================
     FOMO COUNTDOWN TIMER
     Updates every second. Reads end timestamp from data attribute.
     ============================================================ */


  /* ============================================================
     QUICK SHOP MODAL — open / close
     ============================================================ */


  /* ============================================================
     FILTER DRAWER (mobile) — slide in/out
     ============================================================ */


  /* ============================================================
     SORT & FILTER (collection page) — refresh URL params
     ============================================================ */


  /* ============================================================
     STICKY HEADER TRANSFORM (optional scroll-up reveal)
     ============================================================ */


  /* ============================================================
     NEWSLETTER FORM
     Handled inline per-section (see sections/home-newsletter.liquid and
     sections/footer.liquid), each bound to its own <form> element. The
     previous global initNewsletterForms() bound `submit` to a <div>, which
     never fires, so it was dead code and has been removed.
     ============================================================ */


  /* ============================================================
     IMAGE LAZY LOAD (native + polyfill)
     ============================================================ */
  function initLazyImages() {
    if ('loading' in HTMLImageElement.prototype) return; // Native support
    // Intersection Observer fallback
    const imgs = document.querySelectorAll('img[loading="lazy"]');
    if (!imgs.length || !window.IntersectionObserver) return;
    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        if (img.dataset.src) { img.src = img.dataset.src; delete img.dataset.src; }
        observer.unobserve(img);
      });
    }, { rootMargin: '400px' });
    imgs.forEach(function (img) { observer.observe(img); });
  }


  /* ============================================================
     ESC KEY — close all overlays
     ============================================================ */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    // Individual components handle their own Esc — this is a global safety net
    document.body.style.overflow = '';
  });


  /* ============================================================
     EXPOSE PUBLIC API
     ============================================================ */
  window.CHiATO = window.CHiATO || {};
  Object.assign(window.CHiATO, {
    addToCart: addToCart,
    changeCartItem: changeCartItem,
    fetchCart: fetchCart,
    showToast: showToast,
    formatMoney: formatMoney,
    recentlyViewed: RecentlyViewed,
    emit: emit,
  });


  /* ============================================================
     INIT — wait for DOM ready
     ============================================================ */
  document.addEventListener('DOMContentLoaded', function () {
    initSearchOverlay();
    initRecentlyViewed();
    initLazyImages();
  });

}());
